import Fuse from 'fuse.js';

import ngStorage from '../../common/wrappers/ngstorage';
import angularSpinner from '../../common/wrappers/angular-spinner';
import scrollServiceMod from '../common/scroll.service';
import ServicesFilterState, { KEYS } from '../common/session-storage/services-filter-state';

import locationRes from '../common/resources/location.resource';
import personRes from '../common/resources/person.resource';
import serviceRes from '../common/resources/service.resource';
import serviceCategoryRes from '../common/resources/servicecategory.resource';
import ServiceCategory from "../common/models/servicecategory.model";
import ServiceGroup from "../common/models/servicegroup.model";

import textfieldComponentMod from '../../common/material-components/textfields/textfield.component';
import navButtonComponentMod from '../../common/material-components/buttons/nav-button.component';
import normalButtonComponentMod from '../../common/material-components/buttons/normal-button.component';

import servicesFullTpl from './services-full.tpl.html';
import servicesCompactTpl from './services-compact.tpl.html';
import serviceCardComponentMod from './service-card.component';

import './services-widget.scss';

import {IS_MOBILE} from "../common/device-info.utils";
import {combineMatches, doSearch, getSearchTerms} from "../common/search";

const FUSE_CONFIG = {
  id: 'id',
  includeScore: true,
  includeMatches: true,
  threshold: 1,
  location: 0,
  distance: 300,
  maxPatternLength: 32,
  minMatchCharLength: 3,
  keys: [
    {name: "name", weight: 0.99},
    {name: "description", weight: 0.98},
    {name: "category_names", weight: 0.5},
  ]
};

/**
 * Standalone component to display filtered services.
 */
class ServicesComponent {
  constructor(
    $log,
    $httpParamSerializer,
    $q,
    $timeout,
    $window,
    $sessionStorage,
    ServiceResource,
    LocationResource,
    PersonResource,
    ServiceCategoryResource,
    usSpinnerService,
    ScrollService,
    UrlService,
    gettextCatalog
  ) {
    'ngInject';

    this.$log = $log;
    this.$httpParamSerializer = $httpParamSerializer;
    this.$q = $q;
    this.$timeout = $timeout;
    this.$window = $window;
    this.LocationResource = LocationResource;
    this.ServiceResource = ServiceResource;
    this.PersonResource = PersonResource;
    this.ServiceCategoryResource = ServiceCategoryResource;
    this.usSpinnerService = usSpinnerService;
    this.ScrollService = ScrollService;
    this.gettextCatalog = gettextCatalog;
    this.reservationFilterState = new ServicesFilterState($log, $sessionStorage);

    /**
     * Loading locations from the backend?
     * @type {Boolean}
     */
    this.loading = false;

    /**
     * Services retrieved from the backend for display.
     * @type {Array.<Service>}
     */
    this.services = [];

    /**
     * Services to display after all filters have been applied
     * @type {Array.<Service>}
     */
    this.availableServices = [];

    /**
     * Services are searched by this text.
     * @type {String}
     */
    this.serviceSearchText = "";

    /**
     * Services filtered in the template.
     * @type {Array.<Service>}
     */
    this.filteredServices = [];

    /**
     * Options for the order dropdown.
     */
    this.orderOptions = [
      {
        label: this.gettextCatalog.getString('Aakkosjärjestys'),
        value: false
      },
      {
        label: this.gettextCatalog.getString('Etäisyysjärjestys'),
        value: true
      }
    ];

    /**
     * Service that is used for filtering, if given.
     * @type {?Service}
     */
    this.serviceFilter = null;

    this.showInfo = false;
    this.serviceSelectedCallback = null;
    this.serviceInfoCallback = null;

    /**
     * Provided person id to filter by
     * @type {?Number}
     */
    this.personId = null;
    this.person = null;
    /**
     * Provided location id to filter by
     * @type {?Number}
     */
    this.locationId = null;
    this.location = null;

    /**
     * Possible active service. Active selection will be displayed differently.
     * @type {Location}
     */
    this.activeService = null;
    this.activeId = null;

    this.showFilters = !IS_MOBILE;

    /**
     * FuseJS search object; populated when data is received.
     * @type {Fuse<any, Fuse.FuseOptions<any>> | Fuse | null}
     */
    this.fuseSearch = null;

    /**
     * Saved state of the component
     * @type {Object}
     */
    this.savedState = {};

    this.pricesLink = '';
    this.urlService = UrlService;

    this.groupingCategories = [];
  }

  $onInit() {
    this.loading = true;
    this.urlService.pricesUrl().then(url => this.pricesLink = url);

    const state = this.reservationFilterState.getState();
    this.showMoreFilters = state[KEYS.SHOW_MORE_FILTERS];

    this.activeId = this.activeService != null? this.activeService.id : null;

    let promise;
    const promises = [];

    if (this.personId != null) {
      promise = this.PersonResource.getOne(this.personId).then(person => {
        this.person = person;
        this.$log.debug("Got person id in locations. Match person:", this.person);
      });
      promises.push(promise);
    }

    if (this.locationId != null) {
      promise = this.LocationResource.getOne(this.locationId).then(location => {
        this.location = location;
        this.$log.debug("Got lcoation id in services. Match location:", this.location);
      });
      promises.push(promise);
    }

    return this.$q.all(promises)
      .then(() => this.updateServices())
      .finally(() => {
        this.loading = false

        // Scroll to the previously selected service
        if (this.activeService != null) {
          this.$timeout(() =>
            this.ScrollService.navigateToId(`service-${this.activeService.id}`, 0, 0, 'services-list'),
            50);
        }
      });
  }

  populateCategories() {
    return this.ServiceCategoryResource.getMany({ full: true }).then(categoryData => {
      this.categories = categoryData.list;
    })
    .then(() => {
      this.filterCategories = this.categories.map(cat => {
        return { label: cat.name, obj: cat };
      });
      this.groupingCategories = this.filterCategories.slice();
      this.filterCategories.unshift({ label: this.gettextCatalog.getString('Kaikki'), obj: null });
      this.selectedFilter = this.filterCategories[0];
    });
  }

  setGroupingCategories() {
    // Services aren't grouped when they are pre-filtered with location or person
    if (this.location == null && this.person == null) {
      this.groupingCategories = this.groupingCategories.map((category) => {
        const cat = {
          services: this.services.filter(service => service.categories.includes(category.obj.id)).sort(ServicesComponent._sortServicesByOrder)
        }
        return Object.assign(cat, category.obj);
      }).filter(category => category.services.length);
    }
  }

  setupServices(enabledServices, offlineServices=null) {
    this.loading = true;

    return this.$q.all([this.populateCategories(), this.populateServiceList(enabledServices, offlineServices)])
    .then(() => {
      this.services = this.setupSearchTexts(this.services);
      this.fuseSearch = new Fuse(this.services, FUSE_CONFIG);
      return this.updateSearch();
    })
    .then(() => this.setGroupingCategories())
    .finally(() => this.loading = false);
  }

  setupSearchTexts(services) {

    return services.map(s => {
      s.category_names = s.categories.map(_id => {
        const cat = this.categories.find(c => c.id === _id);
        return cat != null ? cat.name : null;
      });

      return s;
    })
  }

  populateServiceList(enabledServices, offlineServices) {
    return this.ServiceResource.getAll().then(services => {
      this.featuredServices = services.list
      .filter(s => s.featured)
      .sort(ServicesComponent._sortServicesByOrder);
      const featuredIds = this.featuredServices.map(s => s.id);

      this.nonFeaturedServices = services.list
      .filter(service => !featuredIds.includes(service.id))
      .sort(ServicesComponent._sortServicesByOrder);

      // Available and unavailable services are calculated differently depending on whether offlineServices
      // is defined or not.
      this.services = services.list
      .map(s => {
        s = s.copy();
        s.available = enabledServices.includes(s.id) && s.internet_reservable;
        s.availableOffline = enabledServices.includes(s.id) && !s.internet_reservable;
        if (offlineServices != null) {
          s.availableOffline = s.availableOffline || offlineServices.includes(s.id);
        }

        s.mismatches = [];
        if (!s.internet_reservable || s.availableOffline) {
          const notAvailableText = this.gettextCatalog.getString('Palvelulle ei voi varata aikaa verkosta.');
          const reserveByPhoneText = this.gettextCatalog.getString('Varaa aika soittamalla <a class="link-no-style" href="tel:+358104003400"><strong>010 400 3400</strong></a>.');
          s.mismatches.push(`${notAvailableText} ${reserveByPhoneText}`);
        }
        else if (!s.available) {
          if (this.person) {
            s.mismatches.push(this.gettextCatalog.getString('Palvelu ei ole tarjolla valitulla henkilöllä <strong>"{{person}}"</strong>.', {
              person: this.person.fullName()
            }))
          }
          else if (this.location) {
            s.mismatches.push(this.gettextCatalog.getString('Palvelu ei ole tarjolla valitussa toimipisteessä <strong>"{{location}}"</strong>.', {
              location: this.location.name
            }))
          }
        }

        return s;
      })
      .sort(ServicesComponent._sortServicesByOrder);

      if (this.location || this.person) {
        this.services = this.services.filter(service => service.available || service.availableOffline);
        this.fuseSearch = new Fuse(this.services, FUSE_CONFIG);
      }

      let availableTitle = '';
      const baseTitle = this.gettextCatalog.getString('Palvelut');
      /// "palvelut ilman verkkoajanvarausta asiantuntijalle X"

      if (this.person) {
        /// "palvelut ilman verkkoajanvarausta asiantuntijalle X"
        availableTitle += `${this.gettextCatalog.getString('asiantuntijalle')} <strong>${this.person.fullName()}</strong>`;
      }
      if (this.location && !this.person) {
        /// "palvelut ilman verkkoajanvarausta toimipisteessä X"
        availableTitle += ` ${this.gettextCatalog.getString('toimipisteessä')} <strong>${this.location.name}</strong>`;
        availableTitle = availableTitle.trim();
      }

      this.resultTitle = `${baseTitle} ${availableTitle}`;

      this.$log.debug("Populated services", this.services);
    });
  }


  updateServices() {
    this.showFeatured = false;

    // Person and/or location selected => mark some services disabled according to what is offered
    // Person services are more important than location service if both are set.
    if (this.person != null) {
      return this.setupServices(this.person.services, this.person.other_services);
    }
    else if (this.location != null) {
      return this.setupServices(this.location.services, this.location.other_services);
    }
    // Show all services as active
    else {
      return this.setupServices([]).then(() => this.showFeatured = true);
    }
  }

  filterServices(services) {
    const searchTerms = getSearchTerms(this.serviceSearchText);
    let searchResults = doSearch(searchTerms, this.fuseSearch);
    // If there were no search results and search was used, exit now as we don't need to show any persons
    // Just an error message with an empty person list
    if (searchTerms.length > 0 && Object.keys(searchResults).length === 0) {
      return services.slice(0,0);
    }
    // Case 2 no search terms
    else if (searchTerms.length === 0) {
      services = services.filter(s => s.available || s.availableOffline);
      for (const service of services) {
        service.score = !service.availableOffline * 0.2 + !service.available * 0.6;
        service.matches = [];
      }
      return services.sort(ServicesComponent._sortServicesByOrder);
    }

    const resultServices = Object.keys(searchResults).map(serviceId => {
      // Service is searched from set of the same service so there has to be a match
      const service = services.find(s=> s.id === parseInt(serviceId, 10));

      // Calculate final scores and show matches
      // score personnel based on matches in service and location if set
      service.score = !service.availableOffline * 0.2 + !service.available * 0.6;

      const result = searchResults[service.id];
      service.score += result.score;
      service.matches = combineMatches(result.matches);

      // Add score (worsen result) for each complete search miss
      // If 3 terms are searched and person has 2 matches, they will get 1 penalty
      // This is the same score as getting complete mismatch from Fuse search
      service.score += searchTerms.length - result.matchCount;

      return service;
    });

    return resultServices.sort(ServicesComponent._sortServicesByOrder);
  }

  catAndGroupFilter() {
    return value => {
      if (!this.selectedFilter) {
        return true;
      }
      return ServicesComponent._matchCategoryOrGroup(this.selectedFilter.obj, value);
    }
  }

  toggleFilters() {
    this.showMoreFilters = !this.showMoreFilters;
    this.reservationFilterState.save(KEYS.SHOW_MORE_FILTERS, this.showMoreFilters)
  }

  onFilterClicked(filter) {
    this.selectedFilter = filter;
    this.reservationFilterState.save(KEYS.CATEGORY, Math.max(0, this.filterCategories.indexOf(this.selectedFilter)))
  }

  onServiceSelected(service) {
    this.serviceSelectedCallback({service});
  }

  onServiceInfoSelected(service) {
    this.serviceInfoCallback({service});
  }

  openChat() {
    this.$window.Ninchat.toggle(true, 'ninchat-oral-customercare');
  }

  showRest(serviceGroup) {
    serviceGroup.hidden = false;
  }

  onSearchTextChanged() {
    this.updateSearch();
  }

  updateSearch() {
    this.availableServices.length = 0;
    const newServices = this.filterServices(this.services);
    this.availableServices.push(...newServices);
  }

  onSearchEnter() {
    this.ScrollService.navigateToId("serviceSearchInput", 75);
  }

  static _matchCategoryOrGroup(selected, value) {
    if (selected instanceof ServiceGroup) {
      return value.groups.includes(selected.id);
    }
    else if (selected instanceof ServiceCategory) {
      return value.categories.includes(selected.id);
    }
    else {
      return true;
    }
  }

  /**
   * Sort services by order number
   *
   * @param {Service} s1
   * @param {Service} s2
   * @return {number} Number for Array sort function
   * @private
   */
  static _sortServicesByOrder(s1, s2) {
    return s1.score - s2.score || s1.order - s2.order;
  }

  /**
   * Sort services by name alphabetically
   *
   * @param {Service} s1
   * @param {Service} s2
   * @return {number} Number for Array sort function
   * @private
   */
  static _sortServicesAlphabetical(s1, s2) {
    return s1.score - s2.score || s1.name.localeCompare(s2.name, 'fi');
  }
}

const config = {
  templateUrl: function($element, $attrs, $log) {
    // Direct templateUrl so that transclude works
    $log.debug('Determining service component template to be used', $attrs.compactMode);

    // Attrs are unevaluated.
    if($attrs.compactMode === "true") {
      return servicesCompactTpl;
    } else {
      return servicesFullTpl;
    }
  },
  controller: ServicesComponent,
  bindings: {
    compactMode: '<', // A more compact layout if displayed inside the service modal
    serviceSelectedCallback: '&',
    showInfo: '<?',
    serviceInfoCallback: '&?',
    locationId: '<?',
    location: '<?',
    personId: '<?',
    person: '<?',
    activeService: '<?',
  }
};

const mod = angular.module('app.services', [
  ngStorage, angularSpinner, scrollServiceMod,
  serviceRes, locationRes, personRes, serviceCategoryRes,
  textfieldComponentMod, navButtonComponentMod, normalButtonComponentMod,
  serviceCardComponentMod,
  ])
  .component('services', config)
;

export default mod.name;
